import { getSignPdfPageScope, SIGN_PDF_SCOPES } from "./signPdfSettings";
import {
  listSignPdfPlacements,
  listSignPdfPlacementsForPage,
  SIGN_PDF_FIELD_KINDS,
} from "./signPdfPlacements";

/** Page thumb colors on the sign progress rail. */
export const SIGN_PDF_RAIL_STATES = {
  GREY: "grey",
  YELLOW: "yellow",
  GREEN: "green",
};

export function pageHasSignPdfPlacements(session, pageNum = 1) {
  return listSignPdfPlacementsForPage(session, pageNum).length > 0;
}

export function pageHasSignPdfSignaturePlacement(session, pageNum = 1) {
  return listSignPdfPlacementsForPage(session, pageNum).some(
    (p) => p.kind === SIGN_PDF_FIELD_KINDS.SIGNATURE
  );
}

export function documentHasSignPdfSignaturePlacement(session, pageNum = 1) {
  const scope = getSignPdfPageScope(session);
  if (scope === SIGN_PDF_SCOPES.PER_PAGE) {
    return pageHasSignPdfSignaturePlacement(session, pageNum);
  }
  return listSignPdfPlacements(session).some(
    (p) => p.kind === SIGN_PDF_FIELD_KINDS.SIGNATURE
  );
}

function isPageConfirmed(
  session,
  pageNum,
  { pageConfirmedMap = {}, allPagesConfirmed = false } = {}
) {
  const scope = getSignPdfPageScope(session);
  if (scope === SIGN_PDF_SCOPES.ALL) {
    return Boolean(allPagesConfirmed);
  }
  if (scope === SIGN_PDF_SCOPES.CURRENT) {
    return Boolean(allPagesConfirmed || pageConfirmedMap[String(pageNum)]);
  }
  return Boolean(pageConfirmedMap[String(pageNum)]);
}

/**
 * Resolve rail color for a page thumb.
 * ALL: all thumbs share grey → yellow → green.
 * CURRENT: only the active page matters; others stay grey.
 * PER_PAGE: per-page grey → yellow → green.
 */
export function getSignPdfPageRailState(
  session,
  pageNum = 1,
  {
    currentPage = 1,
    pageConfirmedMap = {},
    allPagesConfirmed = false,
  } = {}
) {
  const scope = getSignPdfPageScope(session);

  if (isPageConfirmed(session, pageNum, { pageConfirmedMap, allPagesConfirmed })) {
    return SIGN_PDF_RAIL_STATES.GREEN;
  }

  if (scope === SIGN_PDF_SCOPES.ALL) {
    const hasPlacements = listSignPdfPlacements(session).length > 0;
    return hasPlacements
      ? SIGN_PDF_RAIL_STATES.YELLOW
      : SIGN_PDF_RAIL_STATES.GREY;
  }

  if (scope === SIGN_PDF_SCOPES.CURRENT) {
    if (Number(pageNum) !== Number(currentPage)) {
      return SIGN_PDF_RAIL_STATES.GREY;
    }
    return pageHasSignPdfPlacements(session, pageNum)
      ? SIGN_PDF_RAIL_STATES.YELLOW
      : SIGN_PDF_RAIL_STATES.GREY;
  }

  return pageHasSignPdfPlacements(session, pageNum)
    ? SIGN_PDF_RAIL_STATES.YELLOW
    : SIGN_PDF_RAIL_STATES.GREY;
}

export function areAllSignPdfPagesConfirmed(
  session,
  { pageConfirmedMap = {}, allPagesConfirmed = false, pageCount = 0 } = {}
) {
  const scope = getSignPdfPageScope(session);
  const capped = Math.max(0, Number(pageCount) || 0);
  if (capped <= 0) return false;

  if (scope === SIGN_PDF_SCOPES.ALL || scope === SIGN_PDF_SCOPES.CURRENT) {
    return Boolean(allPagesConfirmed);
  }

  for (let p = 1; p <= capped; p += 1) {
    if (!pageConfirmedMap[String(p)]) return false;
  }
  return true;
}

export function shouldShowDoneAllAction(session) {
  return getSignPdfPageScope(session) === SIGN_PDF_SCOPES.ALL;
}

export function canConfirmCurrentPage(
  session,
  pageNum = 1,
  { pageConfirmedMap = {}, allPagesConfirmed = false } = {}
) {
  if (allPagesConfirmed) return false;
  const scope = getSignPdfPageScope(session);
  if (scope === SIGN_PDF_SCOPES.ALL) {
    return (
      documentHasSignPdfSignaturePlacement(session, pageNum) &&
      listSignPdfPlacements(session).length > 0
    );
  }
  if (isPageConfirmed(session, pageNum, { pageConfirmedMap, allPagesConfirmed })) {
    return false;
  }
  return pageHasSignPdfPlacements(session, pageNum);
}

export function railStateClassName(state) {
  if (state === SIGN_PDF_RAIL_STATES.GREEN) return "is-green";
  if (state === SIGN_PDF_RAIL_STATES.YELLOW) return "is-yellow";
  return "is-grey";
}

/**
 * Single page spotlight — yellow (in progress) current page only.
 * Grey = not started, green = done — never animated.
 */
export function shouldSpotlightPageThumb(
  session,
  pageNum,
  {
    currentPage = 1,
    pageConfirmedMap = {},
    allPagesConfirmed = false,
    inPlace = false,
    inReview = false,
  } = {}
) {
  if (!inPlace || inReview) return false;
  if (Number(pageNum) !== Number(currentPage)) return false;
  const state = getSignPdfPageRailState(session, pageNum, {
    currentPage,
    pageConfirmedMap,
    allPagesConfirmed,
  });
  return state === SIGN_PDF_RAIL_STATES.YELLOW;
}

export function connectorClassForRailState(state) {
  if (state === SIGN_PDF_RAIL_STATES.GREEN) return "is-done";
  if (state === SIGN_PDF_RAIL_STATES.YELLOW) return "is-pending";
  return "";
}
